import { create } from 'zustand';
import apiClient from '../lib/api';

export interface WorkDocument {
    id: string;
    tenantId: string;
    title: string;
    content: string;
    folder: string;
    type: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface DocumentState {
    documents: WorkDocument[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchDocuments: (filters?: any) => Promise<void>;
    createDocument: (data: Partial<WorkDocument>) => Promise<void>;
    updateDocument: (id: string, data: Partial<WorkDocument>) => Promise<void>;
    deleteDocument: (id: string) => Promise<void>;
}

export const useWorkDocumentStore = create<DocumentState>((set, get) => ({
    documents: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
    },
    fetchDocuments: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/work/documents', { params: filters });
            const { data, pagination } = response.data;
            set({ documents: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch documents', isLoading: false });
        }
    },
    createDocument: async (data: Partial<WorkDocument>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/work/documents', data);
            const newDocument = response.data;
            set((state) => ({
                documents: [newDocument, ...state.documents].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create document', isLoading: false });
            throw error;
        }
    },
    updateDocument: async (id: string, data: Partial<WorkDocument>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/work/documents/${id}`, data);
            const updatedDocument = response.data;
            set((state) => ({
                documents: state.documents.map((o) => (o.id === id ? updatedDocument : o)).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update document', isLoading: false });
            throw error;
        }
    },
    deleteDocument: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/work/documents/${id}`);
            set((state) => ({ documents: state.documents.filter((d) => d.id !== id) }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete document' });
            throw error;
        }
    },
}));
